/**
 * アカウント情報画面（社長向け・COMPANY_WIDE）。
 * 自社の「1回目支払い内容」「契約内容」（購入時の金額明細）とQRコード発行日を表示する。
 * 金額計算・ラベルは購入フローと同一の calc.ts（*Item ヘルパー）を再利用。表示のみ。
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { organizationApi, type AccountData } from '../admin/api/organizationApi';
import { sortByServiceOrder } from '../purchase/serviceOrder';
import {
  yen,
  annualExcludedOfItem,
  annualIncludedOfItem,
  firstPayBreakdownItem,
  contractBreakdownItem,
} from '../purchase/state/calc';

const Row: React.FC<{ label: React.ReactNode; amount: React.ReactNode; bold?: boolean }> = ({
  label,
  amount,
  bold,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: 0.75,
      borderBottom: '1px solid #eef1f4',
      fontWeight: bold ? 700 : 400,
      fontSize: 14,
    }}
  >
    <span>{label}</span>
    <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{amount}</span>
  </Box>
);

export const AccountInfoPage: React.FC = () => {
  const [data, setData] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    organizationApi
      .getAccount()
      .then(setData)
      .catch(() => setError('アカウント情報の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const items = data.order ? sortByServiceOrder(data.order.items, (i) => i.productCode) : [];
  const firstExcl = items.reduce((s, i) => s + i.amountExcluded, 0);
  const firstIncl = items.reduce((s, i) => s + i.amountIncluded, 0);
  const contractExcl = items.reduce((s, i) => s + annualExcludedOfItem(i), 0);
  const contractIncl = items.reduce((s, i) => s + annualIncludedOfItem(i), 0);

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          会社情報
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 2 }}>
          会社名：{data.companyName}
          <br />
          QRコード発行日：{data.qrIssuedAt || '未設定'}
        </Typography>
      </Paper>

      {data.order ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            1回目支払い内容
          </Typography>
          {items.map((i) => (
            <Row
              key={i.productCode}
              label={
                <>
                  {i.productName}（{firstPayBreakdownItem(i)}）× {i.userCount}名
                </>
              }
              amount={yen(i.amountExcluded)}
            />
          ))}
          <Row label="小計" amount={yen(firstExcl)} />
          <Row label="消費税" amount={yen(firstIncl - firstExcl)} />
          <Row label="合計" amount={yen(firstIncl)} bold />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            契約内容
          </Typography>
          {items.map((i) => (
            <Row
              key={`c-${i.productCode}`}
              label={
                <>
                  {i.productName}（{contractBreakdownItem(i)}）× {i.userCount}名
                </>
              }
              amount={yen(annualExcludedOfItem(i))}
            />
          ))}
          <Row label="小計" amount={yen(contractExcl)} />
          <Row label="消費税" amount={yen(contractIncl - contractExcl)} />
          <Row label="合計" amount={yen(contractIncl)} bold />
        </Paper>
      ) : (
        <Alert severity="info">購入情報がありません。</Alert>
      )}
    </Box>
  );
};

export default AccountInfoPage;
